import type { Dict } from '../../i18n';

const dict: Dict = {
  en: {
    // Topbar
    topbarMeta: 'profile · security · two-factor',
    topbarTitle: 'Settings',

    // Loading / error
    loadingProfile: 'Loading your profile…',

    // Profile card
    profileHeading: 'Profile',
    emailLabel: 'Email',
    roleLabel: 'Role',
    emailRoleNotice: 'Email and role are managed by an administrator and are not self-editable.',
    firstNameLabel: 'First name',
    firstNamePlaceholder: 'María',
    lastNameLabel: 'Last name',
    lastNamePlaceholder: 'López',
    phoneLabel: 'Phone number',
    phonePlaceholder: '+34 600 000 000',
    officeLabel: 'Office',
    officeManagedHint: 'Your office is set by your manager.',
    officePlaceholder: 'Select office',
    profileSaved: 'Profile saved.',
    savingBtn: 'Saving…',
    saveChangesBtn: 'Save changes',

    // Password card
    passwordHeading: 'Password',
    currentPasswordLabel: 'Current password',
    newPasswordLabel: 'New password',
    passwordHint: 'At least 12 characters, with an uppercase letter, a lowercase letter and a digit.',
    confirmPasswordLabel: 'Confirm new password',
    passwordMismatch: 'Passwords do not match.',
    passwordMismatchValidation: 'New password and confirmation do not match.',
    passwordChanged: 'Password changed — you may need to sign in again.',
    passwordSessionsNotice: 'Changing your password signs out your other sessions.',
    updatingBtn: 'Updating…',
    changePasswordBtn: 'Change password',

    // 2FA card (self-service enable/disable for agents)
    twoFactorHeading: 'Two-factor authentication',
    totpApp: 'Authenticator app (TOTP)',
    totpEnabled: 'enabled',
    totpNotEnrolled: 'not enrolled',
    twoFactorOptionalNotice:
      'Two-factor is optional for agents. Enable it for extra account security.',
    twoFactorEnableBtn: 'Enable 2FA',
    twoFactorStarting: 'Starting…',
    twoFactorDisableBtn: 'Disable 2FA',
    twoFactorSecret: 'Secret',
    twoFactorScanQr: 'Scan this QR in your authenticator app',
    twoFactorEnterManually: 'or enter this key manually',
    twoFactorRecoveryCodes: 'Recovery codes — store these now',
    twoFactorCodeLabel: '6-digit code',
    twoFactorConfirmEnable: 'Confirm & enable →',
    twoFactorConfirming: 'Confirming…',
    twoFactorCancel: 'Cancel',
    twoFactorDisableHint: 'Enter a current 6-digit code to confirm disabling 2FA.',
    twoFactorConfirmDisable: 'Disable 2FA',
    twoFactorDisabling: 'Disabling…',
    twoFactorEnabledOk: 'Two-factor authentication enabled.',
    twoFactorDisabledOk: 'Two-factor authentication disabled.',
  },
  es: {
    // Topbar
    topbarMeta: 'perfil · seguridad · doble factor',
    topbarTitle: 'Ajustes',

    // Loading / error
    loadingProfile: 'Cargando tu perfil…',

    // Profile card
    profileHeading: 'Perfil',
    emailLabel: 'Correo electrónico',
    roleLabel: 'Rol',
    emailRoleNotice: 'El correo y el rol están gestionados por un administrador y no son editables.',
    firstNameLabel: 'Nombre',
    firstNamePlaceholder: 'María',
    lastNameLabel: 'Apellido',
    lastNamePlaceholder: 'López',
    phoneLabel: 'Número de teléfono',
    phonePlaceholder: '+34 600 000 000',
    officeLabel: 'Oficina',
    officeManagedHint: 'Tu oficina la asigna tu responsable.',
    officePlaceholder: 'Selecciona la oficina',
    profileSaved: 'Ajustes guardados.',
    savingBtn: 'Guardando…',
    saveChangesBtn: 'Guardar cambios',

    // Password card
    passwordHeading: 'Contraseña',
    currentPasswordLabel: 'Contraseña actual',
    newPasswordLabel: 'Nueva contraseña',
    passwordHint: 'Al menos 12 caracteres, con una letra mayúscula, una minúscula y un número.',
    confirmPasswordLabel: 'Confirmar nueva contraseña',
    passwordMismatch: 'Las contraseñas no coinciden.',
    passwordMismatchValidation: 'La nueva contraseña y la confirmación no coinciden.',
    passwordChanged: 'Contraseña cambiada — puede que necesites iniciar sesión de nuevo.',
    passwordSessionsNotice: 'Cambiar tu contraseña cierra el resto de tus sesiones.',
    updatingBtn: 'Actualizando…',
    changePasswordBtn: 'Cambiar contraseña',

    // 2FA card (autoservicio activar/desactivar para agentes)
    twoFactorHeading: 'Autenticación de doble factor',
    totpApp: 'Aplicación de autenticación (TOTP)',
    totpEnabled: 'Activado',
    totpNotEnrolled: 'no inscrito',
    twoFactorOptionalNotice:
      'El doble factor es opcional para los agentes. Actívalo para mayor seguridad de la cuenta.',
    twoFactorEnableBtn: 'Activar 2FA',
    twoFactorStarting: 'Iniciando…',
    twoFactorDisableBtn: 'Desactivar 2FA',
    twoFactorSecret: 'Secreto',
    twoFactorScanQr: 'Escanea este QR en tu app de autenticación',
    twoFactorEnterManually: 'o introduce esta clave manualmente',
    twoFactorRecoveryCodes: 'Códigos de recuperación — guárdalos ahora',
    twoFactorCodeLabel: 'Código de 6 dígitos',
    twoFactorConfirmEnable: 'Confirmar y activar →',
    twoFactorConfirming: 'Confirmando…',
    twoFactorCancel: 'Cancelar',
    twoFactorDisableHint:
      'Introduce un código actual de 6 dígitos para confirmar la desactivación del 2FA.',
    twoFactorConfirmDisable: 'Desactivar 2FA',
    twoFactorDisabling: 'Desactivando…',
    twoFactorEnabledOk: 'Autenticación de doble factor activada.',
    twoFactorDisabledOk: 'Autenticación de doble factor desactivada.',
  },
  de: {
    // Topbar
    topbarMeta: 'Profil · Sicherheit · Zwei-Faktor',
    topbarTitle: 'Einstellungen',

    // Loading / error
    loadingProfile: 'Ihr Profil lädt…',

    // Profile card
    profileHeading: 'Profil',
    emailLabel: 'E-Mail',
    roleLabel: 'Rolle',
    emailRoleNotice: 'E-Mail und Rolle werden von einem Administrator verwaltet und sind nicht selbst bearbeitbar.',
    firstNameLabel: 'Vorname',
    firstNamePlaceholder: 'María',
    lastNameLabel: 'Nachname',
    lastNamePlaceholder: 'López',
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: '+34 600 000 000',
    officeLabel: 'Niederlassung',
    officeManagedHint: 'Ihre Niederlassung legt Ihr Vorgesetzter fest.',
    officePlaceholder: 'Niederlassung auswählen',
    profileSaved: 'Profil gespeichert.',
    savingBtn: 'Speichert…',
    saveChangesBtn: 'Änderungen speichern',

    // Password card
    passwordHeading: 'Passwort',
    currentPasswordLabel: 'Aktuelles Passwort',
    newPasswordLabel: 'Neues Passwort',
    passwordHint: 'Mindestens 12 Zeichen, mit einem Großbuchstaben, einem Kleinbuchstaben und einer Ziffer.',
    confirmPasswordLabel: 'Neues Passwort bestätigen',
    passwordMismatch: 'Die Passwörter stimmen nicht überein.',
    passwordMismatchValidation: 'Neues Passwort und Bestätigung stimmen nicht überein.',
    passwordChanged: 'Passwort geändert — möglicherweise müssen Sie sich erneut anmelden.',
    passwordSessionsNotice: 'Das Ändern Ihres Passworts meldet Ihre anderen Sitzungen ab.',
    updatingBtn: 'Aktualisiert…',
    changePasswordBtn: 'Passwort ändern',

    // 2FA card (self-service enable/disable for agents)
    twoFactorHeading: 'Zwei-Faktor-Authentifizierung',
    totpApp: 'Authenticator-App (TOTP)',
    totpEnabled: 'aktiviert',
    totpNotEnrolled: 'nicht eingerichtet',
    twoFactorOptionalNotice:
      'Die Zwei-Faktor-Authentifizierung ist für Agenten optional. Aktivieren Sie sie für zusätzliche Kontosicherheit.',
    twoFactorEnableBtn: '2FA aktivieren',
    twoFactorStarting: 'Wird gestartet…',
    twoFactorDisableBtn: '2FA deaktivieren',
    twoFactorSecret: 'Geheimer Schlüssel',
    twoFactorScanQr: 'Scannen Sie diesen QR-Code in Ihrer Authenticator-App',
    twoFactorEnterManually: 'oder geben Sie diesen Schlüssel manuell ein',
    twoFactorRecoveryCodes: 'Wiederherstellungscodes — bewahren Sie sie jetzt auf',
    twoFactorCodeLabel: '6-stelliger Code',
    twoFactorConfirmEnable: 'Bestätigen & aktivieren →',
    twoFactorConfirming: 'Wird bestätigt…',
    twoFactorCancel: 'Abbrechen',
    twoFactorDisableHint: 'Geben Sie einen aktuellen 6-stelligen Code ein, um die Deaktivierung von 2FA zu bestätigen.',
    twoFactorConfirmDisable: '2FA deaktivieren',
    twoFactorDisabling: 'Wird deaktiviert…',
    twoFactorEnabledOk: 'Zwei-Faktor-Authentifizierung aktiviert.',
    twoFactorDisabledOk: 'Zwei-Faktor-Authentifizierung deaktiviert.',
  },
};

export default dict;
